import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import Papa from 'papaparse';
import * as Yup from 'yup';
import { AlertView, TableSkeleton } from 'src/common';
import { useAuth } from 'src/state/auth';
import useWorkspaceVariables from 'src/hooks/useWorkspaceVariables';
import { useAssessmentVariables } from 'src/hooks/useVariables';
import { Scope } from 'src/components/formComposed/WorkspaceVariablesForm/types';
import {
  categorizeVariables,
  transformVariablesToGqlInput,
} from 'src/components/formComposed/WorkspaceVariablesForm/util';
import { CSVGroupSelector, CSVWorkspaceGroupSelector } from 'src/components/VariableCSVReader/CSVGroupSelector';
import {
  CsvImportPreview,
  CsvImportGroups,
  CsvWorkspaceGroups,
  CsvVariable,
  CSVVarHeaders,
} from 'src/components/VariableCSVReader/types';
import { generateCsvTemplateLink, getStandardVariableNames } from 'src/components/VariableCSVReader/util';
import {
  CsvDropzoneWithTemplate,
  CsvValidationErrors,
  CsvUploadButtons,
  SCVSummary,
  DzoneWrap,
} from 'src/components/CsvUpload';
import { ValidationErrorItem } from 'src/utils/csv';
import { pluralize } from 'src/utils/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useCache } from 'src/graphql';
import { useUpsertAssessmentVariablesMutation, useUpsertWorkspaceVariablesMutation } from 'src/graphql/_generated';
import { csvToVariableEdit, csvVariableSchema, filterCsvVariablesByGroups } from './util';
import Button from 'src/components/form/Button';
import styled from 'styled-components';

const pluralizeVariables = (count: number) => pluralize({ count, singular: 'variable', plural: 'variables' });

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
`;

interface FromCSVProps {
  scope: Scope;
  pendingImport: CsvImportPreview | null;
  onPendingImportChange: (preview: CsvImportPreview | null) => void;
  onClose: () => void;
  isModal?: boolean;
}

const standardVariableNames = getStandardVariableNames();

export const FromCSV: React.FC<FromCSVProps> = ({
  scope,
  pendingImport,
  onPendingImportChange,
  onClose,
  isModal = true,
}) => {
  const { variables: wVars } = useWorkspaceVariables();
  const { workspaceId } = useAuth();
  const { assessmentVariables, isAssessmentVariablesLoading } = useAssessmentVariables({
    assessmentId: scope.type === 'assessment' ? scope.id : undefined,
  });

  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [error, setError] = useState('');
  const [uploadSummary, setUploadSummary] = useState<{
    scopeType: 'workspace' | 'assessment';
    existingUpdated?: number;
    newAdded?: number;
    assessmentCustomVars?: number;
    workspaceVarsOverridden?: number;
    alreadyOverriddenUpdated?: number;
    assessmentNewAdded?: number;
  } | null>(null);

  const { refetchQuery } = useCache();
  const [upsertWorkspaceVariables, { loading: workspaceLoading }] = useUpsertWorkspaceVariablesMutation();
  const [upsertAssessmentVariables, { loading: assessmentLoading }] = useUpsertAssessmentVariablesMutation();
  const isUploading = workspaceLoading || assessmentLoading;

  const originalVariables = scope.type === 'assessment' ? assessmentVariables || [] : wVars;
  const workspaceVariableNames = wVars.map((v) => v.name);

  const parseCSV = useCallback(
    (file: File) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const { data, errors: parseErrors } = results;

          const criticalErrors = parseErrors.filter(
            (err) => err.type !== 'FieldMismatch' && err.code !== 'TooFewFields' && err.code !== 'TooManyFields',
          );

          if (criticalErrors.length > 0) {
            setValidationErrors([
              { rowIndex: 0, errors: [{ path: 'File', message: `CSV parsing error: ${criticalErrors[0].message}` }] },
            ]);
            return;
          }

          const errors: ValidationErrorItem[] = [];
          const validVariables: CsvVariable[] = [];

          const seenNames = new Map<string, number>();

          data.forEach((row: unknown, index: number) => {
            const rowData = row as Record<string, string>;

            try {
              csvVariableSchema.validateSync(rowData, { abortEarly: false });

              const variableName = rowData[CSVVarHeaders.Name].trim();

              const normalizedName = variableName.toLowerCase();
              if (seenNames.has(normalizedName)) {
                const firstRowIndex = seenNames.get(normalizedName)!;
                errors.push({
                  rowIndex: index + 1,
                  errors: [
                    {
                      path: CSVVarHeaders.Name,
                      message: `Duplicate variable name "${variableName}"`,
                    },
                  ],
                });
                return;
              }
              seenNames.set(normalizedName, index + 1);

              const originalVariable = originalVariables.find((v) => v.name === variableName);
              const isStandardVariable = standardVariableNames.includes(variableName);

              let currencyValue = false;
              if (isStandardVariable && originalVariable) {
                currencyValue = originalVariable.currency;
              } else {
                currencyValue = rowData[CSVVarHeaders['Currency (yes/no)']] === 'yes';
              }

              const rawValue = rowData[CSVVarHeaders.Value];
              const parsedValue = rawValue === '' || rawValue === undefined ? undefined : Number(rawValue);

              validVariables.push({
                name: variableName,
                value: parsedValue,
                currency: currencyValue,
              });
            } catch (validationError) {
              if (validationError instanceof Yup.ValidationError) {
                errors.push({
                  rowIndex: index + 1,
                  errors: validationError.inner.map((err) => ({
                    path: err.path || 'Unknown',
                    message: err.message,
                  })),
                });
              }
            }
          });

          if (errors.length > 0) {
            setValidationErrors(errors);
            onPendingImportChange(null);
          } else {
            setValidationErrors([]);

            let toUpdate = 0;
            let toCreate = 0;
            let toOverride = 0;
            const changedVariables: CsvVariable[] = [];

            const groups: CsvImportGroups = {
              customAssessmentVars: { variables: [], action: 'include' },
              workspaceVars: { variables: [], action: 'include' },
              overriddenVars: { variables: [], action: 'include' },
              newVars: { variables: [], action: 'include' },
            };

            const workspaceGroups: CsvWorkspaceGroups = {
              existingVars: { variables: [], action: 'include' },
              newVars: { variables: [], action: 'include' },
            };

            validVariables.forEach((csvVar) => {
              const originalVar = originalVariables.find((v) => v.name === csvVar.name);
              const existsInOriginal = !!originalVar;
              const existsInWorkspace = workspaceVariableNames.includes(csvVar.name);

              const originalValue = originalVar?.value;
              const csvValue = csvVar.value;

              const isValueSame =
                originalVar && (originalValue === csvValue || (originalValue == null && csvValue == null));
              const isCurrencySame = originalVar && originalVar.currency === csvVar.currency;
              const isUnchanged = isValueSame && isCurrencySame;

              if (scope.type === 'assessment') {
                if (existsInOriginal && existsInWorkspace) {
                  if (!isUnchanged) {
                    toUpdate++;
                    changedVariables.push(csvVar);
                    groups.overriddenVars.variables.push(csvVar);
                  }
                } else if (existsInOriginal && !existsInWorkspace) {
                  if (!isUnchanged) {
                    toUpdate++;
                    changedVariables.push(csvVar);
                    groups.customAssessmentVars.variables.push(csvVar);
                  }
                } else if (!existsInOriginal && existsInWorkspace) {
                  toOverride++;
                  changedVariables.push(csvVar);
                  groups.workspaceVars.variables.push(csvVar);
                } else {
                  toCreate++;
                  changedVariables.push(csvVar);
                  groups.newVars.variables.push(csvVar);
                }
              } else {
                if (existsInOriginal) {
                  if (!isUnchanged) {
                    toUpdate++;
                    changedVariables.push(csvVar);
                    workspaceGroups.existingVars.variables.push(csvVar);
                  }
                } else {
                  toCreate++;
                  changedVariables.push(csvVar);
                  workspaceGroups.newVars.variables.push(csvVar);
                }
              }
            });

            const preview: CsvImportPreview = {
              variables: changedVariables,
              fileName: file.name,
              summary: {
                total: changedVariables.length,
                toUpdate,
                toCreate,
                toOverride,
              },
              groups: scope.type === 'assessment' ? groups : undefined,
              workspaceGroups: scope.type === 'workspace' ? workspaceGroups : undefined,
            };

            onPendingImportChange(preview);
          }
        },
        error: (error) => {
          setValidationErrors([
            { rowIndex: 0, errors: [{ path: 'File', message: `CSV parsing error: ${error.message}` }] },
          ]);
        },
      });
    },
    [
      originalVariables,
      workspaceVariableNames,
      scope.type,
      onPendingImportChange,
      csvVariableSchema,
      standardVariableNames,
    ],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      mpEvent(MPEvents.FileLoaded, {
        modal: 'Variables modal',
        tags: ['VARIABLES'],
      });
      if (acceptedFiles?.length > 0) {
        const selectedFile = acceptedFiles[0];
        setFile(selectedFile);
        setValidationErrors([]);
        setIsUploadSuccessful(false);
        setError('');
        parseCSV(selectedFile);
      }
    },
    [parseCSV],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
  });

  const handleGroupsChange = (groups: CsvImportGroups) => {
    if (pendingImport) {
      onPendingImportChange({ ...pendingImport, groups });
    }
  };

  const handleWorkspaceGroupsChange = (workspaceGroups: CsvWorkspaceGroups) => {
    if (pendingImport) {
      onPendingImportChange({ ...pendingImport, workspaceGroups });
    }
  };

  const resetUpload = () => {
    setFile(null);
    setValidationErrors([]);
    setIsUploadSuccessful(false);
    setError('');
    setUploadSummary(null);
    onPendingImportChange(null);
  };

  const save = async () => {
    if (!pendingImport) {
      return;
    }

    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Variables modal',
      tags: ['VARIABLES'],
    });

    setError('');

    try {
      const filteredCsvVariables = filterCsvVariablesByGroups(
        pendingImport.variables,
        pendingImport.groups,
        pendingImport.workspaceGroups,
      );

      if (filteredCsvVariables.length === 0) {
        setUploadSummary(
          scope.type === 'workspace'
            ? { scopeType: 'workspace', existingUpdated: 0, newAdded: 0 }
            : {
                scopeType: 'assessment',
                assessmentCustomVars: 0,
                workspaceVarsOverridden: 0,
                alreadyOverriddenUpdated: 0,
                assessmentNewAdded: 0,
              },
        );
        setIsUploadSuccessful(true);
        onPendingImportChange(null);
        console.groupEnd();
        return;
      }

      const variablesToSave = csvToVariableEdit(filteredCsvVariables, scope, wVars, originalVariables);

      const promises: Promise<unknown>[] = [];

      if (scope.type === 'workspace') {
        const csvVariableNames = new Set(variablesToSave.map((v) => v.name));
        const relevantOriginals = originalVariables.filter((ov) => csvVariableNames.has(ov.name));
        const categorized = categorizeVariables(variablesToSave, relevantOriginals, [], 'workspace');

        if (categorized.length) {
          const gqlInput = transformVariablesToGqlInput(categorized);

          promises.push(
            upsertWorkspaceVariables({
              variables: {
                workspaceId: workspaceId!,
                variables: gqlInput,
              },
            }),
          );
        }
      } else {
        const assessmentVars = variablesToSave.filter((v) => v._isNewOverwrite || v._isNew || v._isFromCSV);

        if (assessmentVars.length > 0) {
          const csvVariableNames = new Set(assessmentVars.map((v) => v.name));
          const relevantOriginals = originalVariables.filter((ov) => csvVariableNames.has(ov.name));
          const avCategorized = categorizeVariables(assessmentVars, relevantOriginals, [], 'assessment');

          if (avCategorized.length) {
            const gqlInput = transformVariablesToGqlInput(avCategorized);

            promises.push(
              upsertAssessmentVariables({
                variables: {
                  assessmentId: scope.id,
                  variables: gqlInput,
                },
              }),
            );
          }
        }
      }

      if (promises.length === 0) {
        setUploadSummary(
          scope.type === 'workspace'
            ? { scopeType: 'workspace', existingUpdated: 0, newAdded: 0 }
            : {
                scopeType: 'assessment',
                assessmentCustomVars: 0,
                workspaceVarsOverridden: 0,
                alreadyOverriddenUpdated: 0,
                assessmentNewAdded: 0,
              },
        );
        setIsUploadSuccessful(true);
        onPendingImportChange(null);
        console.groupEnd();
        return;
      }

      await Promise.all(promises);
      queryCache.invalidateQueries([QKeys.Audit]);
      if (scope.type === 'assessment') {
        await refetchQuery({ include: ['workspaceVariables', 'assessment', 'assessmentRiskRegister', 'scenario'] });
      } else {
        await refetchQuery({ include: ['workspaceVariables', 'assessmentsList'] });
      }

      if (scope.type === 'workspace') {
        const existingUpdated =
          pendingImport.workspaceGroups?.existingVars.action === 'include'
            ? pendingImport.workspaceGroups.existingVars.variables.filter((v) =>
                filteredCsvVariables.some((fc) => fc.name === v.name),
              ).length
            : 0;
        const newAdded =
          pendingImport.workspaceGroups?.newVars.action === 'include'
            ? pendingImport.workspaceGroups.newVars.variables.filter((v) =>
                filteredCsvVariables.some((fc) => fc.name === v.name),
              ).length
            : 0;

        setUploadSummary({ scopeType: 'workspace', existingUpdated, newAdded });
      } else {
        const assessmentCustomVars =
          pendingImport.groups?.customAssessmentVars.action === 'include'
            ? pendingImport.groups.customAssessmentVars.variables.filter((v) =>
                filteredCsvVariables.some((fc) => fc.name === v.name),
              ).length
            : 0;
        const workspaceVarsOverridden =
          pendingImport.groups?.workspaceVars.action === 'include'
            ? pendingImport.groups.workspaceVars.variables.filter((v) =>
                filteredCsvVariables.some((fc) => fc.name === v.name),
              ).length
            : 0;
        const alreadyOverriddenUpdated =
          pendingImport.groups?.overriddenVars.action === 'include'
            ? pendingImport.groups.overriddenVars.variables.filter((v) =>
                filteredCsvVariables.some((fc) => fc.name === v.name),
              ).length
            : 0;
        const assessmentNewAdded =
          pendingImport.groups?.newVars.action === 'include'
            ? pendingImport.groups.newVars.variables.filter((v) =>
                filteredCsvVariables.some((fc) => fc.name === v.name),
              ).length
            : 0;

        setUploadSummary({
          scopeType: 'assessment',
          assessmentCustomVars,
          workspaceVarsOverridden,
          alreadyOverriddenUpdated,
          assessmentNewAdded,
        });
      }
      setIsUploadSuccessful(true);
      onPendingImportChange(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  const workspaceExistingVariables =
    pendingImport?.workspaceGroups?.existingVars.action === 'include'
      ? pendingImport.workspaceGroups.existingVars.variables.length
      : 0;
  const workspaceNewVariables = pendingImport?.workspaceGroups?.newVars.variables.length || 0;

  const assessmentCustomVars =
    pendingImport?.groups?.customAssessmentVars.action === 'include'
      ? pendingImport.groups.customAssessmentVars.variables.length
      : 0;
  const assessmentWorkspaceVars =
    pendingImport?.groups?.workspaceVars.action === 'include' ? pendingImport.groups.workspaceVars.variables.length : 0;
  const assessmentOverriddenVars =
    pendingImport?.groups?.overriddenVars.action === 'include'
      ? pendingImport.groups.overriddenVars.variables.length
      : 0;
  const assessmentNewVars =
    pendingImport?.groups?.newVars.action === 'include' ? pendingImport.groups.newVars.variables.length : 0;

  if (isAssessmentVariablesLoading) {
    return (
      <DzoneWrap className="h-padding">
        <TableSkeleton />
      </DzoneWrap>
    );
  }

  return (
    <DzoneWrap className="h-padding">
      <CsvDropzoneWithTemplate
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isDragActive={isDragActive}
        file={file}
        templateLink={generateCsvTemplateLink(scope.type)}
        templateFileName="variables_template.csv"
        onTemplateClick={() =>
          mpEvent(MPEvents.DownloadCSVTemplate, {
            modal: 'Variables modal',
            tags: ['VARIABLES'],
          })
        }
      />

      <CsvValidationErrors errors={validationErrors} />

      {pendingImport?.groups &&
        (pendingImport.groups.customAssessmentVars.variables.length > 0 ||
          pendingImport.groups.workspaceVars.variables.length > 0 ||
          pendingImport.groups.overriddenVars.variables.length > 0) && (
          <AlertView>
            <CSVGroupSelector groups={pendingImport.groups} onGroupsChange={handleGroupsChange} />
          </AlertView>
        )}

      {pendingImport?.workspaceGroups && pendingImport.workspaceGroups.existingVars.variables.length > 0 && (
        <AlertView>
          <CSVWorkspaceGroupSelector
            groups={pendingImport.workspaceGroups}
            onGroupsChange={handleWorkspaceGroupsChange}
          />
        </AlertView>
      )}

      {file && !isUploadSuccessful && (
        <>
          {pendingImport?.groups ? (
            <SCVSummary
              items={[
                'File validated',
                `${assessmentCustomVars} existing assessment ${pluralizeVariables(assessmentCustomVars)}`,
                `${assessmentWorkspaceVars} workspace ${pluralizeVariables(assessmentWorkspaceVars)} to override`,
                `${assessmentOverriddenVars} already overridden ${pluralizeVariables(assessmentOverriddenVars)}`,
                `${assessmentNewVars} new ${pluralizeVariables(assessmentNewVars)} to be added`,
              ]}
            />
          ) : pendingImport?.workspaceGroups ? (
            <SCVSummary
              items={[
                'File validated',
                `${workspaceExistingVariables} existing ${pluralizeVariables(workspaceExistingVariables)} ready`,
                `${workspaceNewVariables} new ${pluralizeVariables(workspaceNewVariables)} to be added`,
              ]}
            />
          ) : null}
        </>
      )}

      {isUploadSuccessful && uploadSummary && uploadSummary.scopeType === 'workspace' && (
        <SCVSummary
          isCompleted
          items={[
            `${uploadSummary.existingUpdated} existing ${pluralizeVariables(uploadSummary.existingUpdated || 0)} updated`,
            `${uploadSummary.newAdded} new ${pluralizeVariables(uploadSummary.newAdded || 0)} added`,
            'File upload successful',
          ]}
        />
      )}
      {isUploadSuccessful && uploadSummary && uploadSummary.scopeType === 'assessment' && (
        <SCVSummary
          isCompleted
          items={[
            `${uploadSummary.assessmentCustomVars} existing assessment ${pluralizeVariables(uploadSummary.assessmentCustomVars || 0)} updated`,
            `${uploadSummary.workspaceVarsOverridden} workspace ${pluralizeVariables(uploadSummary.workspaceVarsOverridden || 0)} overridden`,
            `${uploadSummary.alreadyOverriddenUpdated} already overridden ${pluralizeVariables(uploadSummary.alreadyOverriddenUpdated || 0)} updated`,
            `${uploadSummary.assessmentNewAdded} new ${pluralizeVariables(uploadSummary.assessmentNewAdded || 0)} added`,
            'File upload successful',
          ]}
        />
      )}

      {isModal ? (
        <CsvUploadButtons
          isUploadSuccessful={isUploadSuccessful}
          hasFile={!!file}
          isDisabled={!file || !!validationErrors.length || isUploading || pendingImport?.variables.length === 0}
          isUploading={isUploading}
          onCancel={() => {
            onClose();
            resetUpload();
          }}
          onUpload={save}
          onSelectFile={open}
          onUploadAnother={resetUpload}
          onDone={() => {
            onClose();
            resetUpload();
          }}
        />
      ) : (
        <Controls>
          {isUploadSuccessful ? (
            <Button primary onClick={resetUpload} $constWidth={220}>
              UPLOAD ANOTHER
            </Button>
          ) : (
            <Button
              primary
              onClick={save}
              disabled={!file || !!validationErrors.length || isUploading || pendingImport?.variables.length === 0}
              $constWidth={220}
            >
              UPLOAD
            </Button>
          )}
        </Controls>
      )}

      {error && <div className="error">{error}</div>}
    </DzoneWrap>
  );
};
